import React, { useState } from 'react';
import { ArrowLeft, Target, PieChart, TrendingUp, Shield, CheckCircle, Calculator, Users, Clock, BookOpen } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Slider } from '../components/ui/slider';

const FinancialPlanningPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  
  // Emergency Fund Calculator State
  const [monthlyExpenses, setMonthlyExpenses] = useState({
    housing: 1100,
    food: 10000,
    healthCare: 2000,
    utilities: 10000,
    transportation: 3000,
    personalExpenses: 5000,
    debtAndInvestments: 10000
  });

  // Tax Saving Calculator State
  const [investmentAmount, setInvestmentAmount] = useState(10000);

  const planningServices = [
    {
      icon: <Target size={32} />,
      title: "Goal-Based Planning",
      description: "Structured approach to achieve specific financial objectives like buying a home, children's education, or dream vacation.",
      features: [
        "Define clear financial goals",
        "Timeline-based planning",
        "Investment strategy alignment",
        "Regular progress tracking"
      ]
    },
    {
      icon: <Clock size={32} />,
      title: "Retirement Planning",
      description: "Ensure a comfortable and financially secure retirement with systematic wealth accumulation strategies.",
      features: [
        "Retirement corpus calculation",
        "Pension plan optimization",
        "Tax-efficient strategies",
        "Inflation-adjusted planning"
      ]
    },
    {
      icon: <Calculator size={32} />,
      title: "Tax Optimization",
      description: "Minimize tax liability through smart investment choices and strategic financial planning.",
      features: [
        "Section 80C optimization",
        "Tax-efficient investments",
        "HRA and other deductions",
        "Capital gains planning"
      ]
    },
    {
      icon: <Shield size={32} />,
      title: "Estate Planning",
      description: "Protect and transfer your wealth to the next generation with proper estate planning strategies.",
      features: [
        "Will and testament guidance",
        "Trust structure planning",
        "Succession planning",
        "Wealth transfer strategies"
      ]
    }
  ];

  // Mobile detection
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Emergency Fund Calculator Functions
  const calculateEmergencyFund = () => {
    const totalMonthlyExpenses = Object.values(monthlyExpenses).reduce((sum, expense) => sum + expense, 0);
    const emergencyFundAmount = totalMonthlyExpenses * 6; // 6 months of expenses
    return {
      totalMonthlyExpenses,
      emergencyFundAmount
    };
  };

  // Handle button actions based on device
  const handleScheduleConsultation = () => {
    if (isMobile) {
      window.location.href = 'tel:+919904449922';
    } else {
      navigate('/#contact');
    }
  };

  const handleGetFinancialPlanQuote = () => {
    window.location.href = 'mailto:nitumf2017@gmail.com';
  };

  // Tax Saving Calculator Functions
  const calculateTaxSavings = () => {
    const maxDeduction = Math.min(investmentAmount, 150000); // Section 80C limit is 1.5L
    
    return {
      tax5Percent: Math.round(maxDeduction * 0.054),
      tax20Percent: Math.round(maxDeduction * 0.206),
      tax30Percent: Math.round(maxDeduction * 0.309)
    };
  };

  const planningProcess = [
    {
      step: "1",
      title: "Financial Health Check",
      description: "Comprehensive analysis of your current financial situation, assets, liabilities, and cash flow patterns."
    },
    {
      step: "2", 
      title: "Goal Identification",
      description: "Define and prioritize your short-term and long-term financial goals with realistic timelines."
    },
    {
      step: "3",
      title: "Strategy Development", 
      description: "Create customized investment and savings strategies aligned with your goals and risk profile."
    },
    {
      step: "4",
      title: "Implementation",
      description: "Put the financial plan into action with systematic investments and regular monitoring."
    },
    {
      step: "5",
      title: "Review & Rebalance",
      description: "Regular review and adjustment of your financial plan based on life changes and market conditions."
    }
  ];

  const lifeGoals = [
    {
      title: "Child's Education",
      description: "Plan for rising education costs with systematic investments",
      timeframe: "10-20 years",
      strategy: "Equity-focused SIPs with step-up"
    },
    {
      title: "Home Purchase", 
      description: "Save for down payment and manage home loan efficiently",
      timeframe: "3-7 years",
      strategy: "Balanced debt-equity allocation"  
    },
    {
      title: "Retirement",
      description: "Build sufficient corpus for post-retirement lifestyle",
      timeframe: "20-30 years", 
      strategy: "Long-term equity with debt transition"
    },
    {
      title: "Emergency Fund",
      description: "Create safety net for unexpected expenses",
      timeframe: "Immediate",
      strategy: "Liquid funds and savings account"
    }
  ];

  const taxSavingTips = [
    "Start tax planning early in the financial year",
    "Use monthly SIPs for better cash flow management", 
    "Diversify across different tax-saving instruments",
    "Consider tax-efficient investment avenues",
    "Plan for both short-term and long-term tax savings",
    "Review and optimize your tax strategy annually"
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Breadcrumb Navigation */}
      <div className="bg-light-gold py-4">
        <div className="container">
          <div className="flex items-center gap-3 text-sm">
            <Link to="/" className="text-primary-green hover:text-primary-gold transition-colors">
              Home
            </Link>
            <span className="text-medium-gray">/</span>
            <span className="text-primary-green font-medium">Financial Planning & Wealth Guidance</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="section-padding bg-gradient-hero">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Link 
                to="/" 
                className="btn-secondary inline-flex items-center gap-2 hover:scale-105 transition-transform"
              >
                <ArrowLeft size={18} />
                Back to Home
              </Link>
            </div>
            
            <div className="text-primary-gold mb-4">
              <Target size={64} className="mx-auto" />
            </div>
            
            <h1 className="heading-hero mb-6">
              Financial Planning & <span className="text-primary-gold">Wealth Guidance</span>
            </h1>
            
            <p className="text-large mb-8 max-w-3xl mx-auto">
              Plan for your tomorrow, today. Whether you're building wealth or securing your legacy—our strategic advisement keeps your goals front and center.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button 
                onClick={() => document.getElementById('contact-section').scrollIntoView({ behavior: 'smooth' })}
                className="btn-primary"
              >
                <Target size={20} />
                Start Financial Planning
              </button>
              <button 
                onClick={() => document.getElementById('planning-process').scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary"
              >
                Learn Our Process
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What is Financial Planning */}
      <section className="section-padding">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <h2 className="heading-section mb-6">What is Financial Planning?</h2>
              <div className="space-y-4 text-body">
                <p>
                  Financial planning is the process of organizing your finances to achieve your life goals in a systematic and disciplined manner. It involves creating a roadmap for your financial future.
                </p>
                <p>
                  Effective financial planning helps you understand your current financial position, define your goals, and create strategies to achieve them while managing risks and optimizing returns.
                </p>
                <p>
                  It's not just about investing money - it's about making informed decisions that align your financial resources with your life aspirations and ensuring financial security for you and your family.
                </p>
              </div>
            </div>
            
            <div className="fade-in-up delay-2">
              <div className="bg-cream-bg rounded-3xl p-8 border-2 border-primary-gold border-opacity-20">
                <h3 className="heading-card mb-6 text-center">Planning Benefits</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-white rounded-xl">
                    <CheckCircle size={20} className="text-primary-gold flex-shrink-0" />
                    <span className="text-body">Clear financial roadmap</span>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-white rounded-xl">
                    <CheckCircle size={20} className="text-primary-gold flex-shrink-0" />
                    <span className="text-body">Goal-oriented investments</span>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-white rounded-xl">
                    <CheckCircle size={20} className="text-primary-gold flex-shrink-0" />
                    <span className="text-body">Risk management strategies</span>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-white rounded-xl">
                    <CheckCircle size={20} className="text-primary-gold flex-shrink-0" />
                    <span className="text-body">Tax optimization</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Planning Services */}
      <section className="section-padding bg-gradient-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-section mb-6">Our Financial Planning Services</h2>
            <p className="text-large max-w-3xl mx-auto">
              Comprehensive financial planning services designed to help you achieve every financial milestone
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {planningServices.map((service, index) => (
              <div key={index} className="service-card fade-in-up" style={{animationDelay: `${index * 0.2}s`}}>
                <div className="text-primary-gold mb-4">
                  {service.icon}
                </div>
                <h3 className="heading-card mb-4">{service.title}</h3>
                <p className="text-body mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary-gold rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-body text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Planning Process */}
      <section id="planning-process" className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-section mb-6">Our Financial Planning Process</h2>
            <p className="text-large max-w-3xl mx-auto">
              A systematic 5-step approach to create and implement your personalized financial plan
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {planningProcess.map((process, index) => (
                <div key={index} className={`grid md:grid-cols-12 gap-8 items-center fade-in-up ${index % 2 === 1 ? 'md:direction-rtl' : ''}`} style={{animationDelay: `${index * 0.2}s`}}>
                  <div className={`md:col-span-3 ${index % 2 === 1 ? 'md:order-2' : ''}`}>
                    <div className="w-24 h-24 bg-primary-gold rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto">
                      {process.step}
                    </div>
                  </div>
                  <div className={`md:col-span-9 ${index % 2 === 1 ? 'md:order-1 md:text-right' : ''}`}>
                    <h3 className="heading-card mb-4">{process.title}</h3>
                    <p className="text-body">{process.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Life Goals Planning */}
      <section className="section-padding bg-gradient-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-section mb-6">Plan for Life's Important Goals</h2>
            <p className="text-large max-w-3xl mx-auto">
              Every life goal requires a different investment approach. Let us help you plan for each milestone.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {lifeGoals.map((goal, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 fade-in-up" style={{animationDelay: `${index * 0.2}s`}}>
                <h3 className="heading-card mb-4">{goal.title}</h3>
                <p className="text-body mb-6">{goal.description}</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-light-gold rounded-xl p-4">
                    <div className="text-sm text-secondary mb-1">Timeframe</div>
                    <div className="font-semibold text-primary-green">{goal.timeframe}</div>
                  </div>
                  <div className="bg-cream-bg rounded-xl p-4">
                    <div className="text-sm text-secondary mb-1">Strategy</div>
                    <div className="font-semibold text-primary-green text-sm">{goal.strategy}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tax Saving Section */}
      <section className="section-padding">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <h2 className="heading-section mb-6">Smart Tax Saving Strategies</h2>
              <p className="text-body mb-6">
                "Minimize the amount of tax you pay with prudent tax saving." There is more to tax saving than the exemptions available on investment made by you. With right investments, you will pay the right amount of tax.
              </p>
              
              <div className="space-y-4">
                {taxSavingTips.map((tip, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-primary-gold mt-1 flex-shrink-0" />
                    <span className="text-body">{tip}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="fade-in-up delay-2">
              <div className="bg-primary-green rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-poppins font-semibold mb-6 text-center">
                  Check How Much You Can Save
                </h3>
                
                {/* Investment Amount Input */}
                <div className="mb-6">
                  <label className="text-sm font-medium block mb-3 text-center">Select your investment amount</label>
                  <input
                    type="number"
                    value={investmentAmount}
                    onChange={(e) => setInvestmentAmount(Number(e.target.value) || 0)}
                    placeholder="Enter amount"
                    className="w-full p-3 rounded-xl bg-white bg-opacity-20 text-white border border-white border-opacity-30 focus:outline-none focus:ring-2 focus:ring-primary-gold placeholder-white placeholder-opacity-50 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>

                {/* Tax Savings Results */}
                {(() => {
                  const savings = calculateTaxSavings();
                  return (
                    <div className="space-y-4">
                      <div className="text-center mb-4">
                        <div className="text-sm opacity-90 mb-2">Depending on your tax-slab, your savings under Sec 80C</div>
                        <div className="text-2xl font-bold text-primary-gold">
                          ₹{savings.tax30Percent.toLocaleString('en-IN')}
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between items-center bg-white bg-opacity-10 rounded-xl p-3">
                          <span className="text-sm">If you are in 5% Slab</span>
                          <span className="text-primary-gold font-semibold">₹{savings.tax5Percent.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between items-center bg-white bg-opacity-10 rounded-xl p-3">
                          <span className="text-sm">If you are in 20% Slab</span>
                          <span className="text-primary-gold font-semibold">₹{savings.tax20Percent.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between items-center bg-white bg-opacity-10 rounded-xl p-3">
                          <span className="text-sm">If you are in 30% Slab</span>
                          <span className="text-primary-gold font-semibold">₹{savings.tax30Percent.toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Fund Calculator Section */}
      <section className="section-padding bg-gradient-section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <h2 className="heading-section mb-6">How Much Should You Have Saved?</h2>
              <p className="text-body mb-6">
              Most experts believe you should have enough money in your emergency fund to cover at least 6 to 8 months' worth of living expenses. What does that look like? Start by estimating your costs for essential expenses, such as: Housing, Food, Health care (including insurance) Utilities, Transportation, Personal expenses, Debt servicing (EMIs). You don't need to include expenses for anything you'd cut from your budget in the event of a job loss or major catastrophe. For example: Entertainment, Dining out, Nonessential shopping, Vacations, Savings for a second home or other expenses. Decide if you need to save more. Putting aside 6 to 8 months' worth of expenses is a good rule of thumb, but sometimes it's not enough. If you're able, you might want to think about expanding your emergency savings.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-primary-gold mt-1 flex-shrink-0" />
                  <span className="text-body">Protects against unexpected job loss or medical emergencies</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-primary-gold mt-1 flex-shrink-0" />
                  <span className="text-body">Keeps your long-term investments intact during crises</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-primary-gold mt-1 flex-shrink-0" />
                  <span className="text-body">Provides peace of mind and financial security</span>
                </div>
              </div>
            </div>
            
            <div className="fade-in-up delay-2">
              <div className="bg-primary-green rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-poppins font-semibold mb-6 text-center">
                  Emergency Fund Calculator
                </h3>
                
                {/* Monthly Expenses Inputs */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Housing</label>
                    <input
                      type="number"
                      value={monthlyExpenses.housing}
                      onChange={(e) => setMonthlyExpenses(prev => ({...prev, housing: Number(e.target.value) || 0}))}
                      placeholder="Enter amount"
                      className="w-32 p-2 rounded-lg bg-white bg-opacity-20 text-white border border-white border-opacity-30 focus:outline-none focus:ring-2 focus:ring-primary-gold placeholder-white placeholder-opacity-50 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Food</label>
                    <input
                      type="number"
                      value={monthlyExpenses.food}
                      onChange={(e) => setMonthlyExpenses(prev => ({...prev, food: Number(e.target.value) || 0}))}
                      placeholder="Enter amount"
                      className="w-32 p-2 rounded-lg bg-white bg-opacity-20 text-white border border-white border-opacity-30 focus:outline-none focus:ring-2 focus:ring-primary-gold placeholder-white placeholder-opacity-50 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Health Care (including insurance)</label>
                    <input
                      type="number"
                      value={monthlyExpenses.healthCare}
                      onChange={(e) => setMonthlyExpenses(prev => ({...prev, healthCare: Number(e.target.value) || 0}))}
                      placeholder="Enter amount"
                      className="w-32 p-2 rounded-lg bg-white bg-opacity-20 text-white border border-white border-opacity-30 focus:outline-none focus:ring-2 focus:ring-primary-gold placeholder-white placeholder-opacity-50 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Utilities (water, electricity, etc.)</label>
                    <input
                      type="number"
                      value={monthlyExpenses.utilities}
                      onChange={(e) => setMonthlyExpenses(prev => ({...prev, utilities: Number(e.target.value) || 0}))}
                      placeholder="Enter amount"
                      className="w-32 p-2 rounded-lg bg-white bg-opacity-20 text-white border border-white border-opacity-30 focus:outline-none focus:ring-2 focus:ring-primary-gold placeholder-white placeholder-opacity-50 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Transportation</label>
                    <input
                      type="number"
                      value={monthlyExpenses.transportation}
                      onChange={(e) => setMonthlyExpenses(prev => ({...prev, transportation: Number(e.target.value) || 0}))}
                      placeholder="Enter amount"
                      className="w-32 p-2 rounded-lg bg-white bg-opacity-20 text-white border border-white border-opacity-30 focus:outline-none focus:ring-2 focus:ring-primary-gold placeholder-white placeholder-opacity-50 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Personal Expenses</label>
                    <input
                      type="number"
                      value={monthlyExpenses.personalExpenses}
                      onChange={(e) => setMonthlyExpenses(prev => ({...prev, personalExpenses: Number(e.target.value) || 0}))}
                      placeholder="Enter amount"
                      className="w-32 p-2 rounded-lg bg-white bg-opacity-20 text-white border border-white border-opacity-30 focus:outline-none focus:ring-2 focus:ring-primary-gold placeholder-white placeholder-opacity-50 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Debt Servicing (EMIs) & Investments (SIPs, etc.)</label>
                    <input
                      type="number"
                      value={monthlyExpenses.debtAndInvestments}
                      onChange={(e) => setMonthlyExpenses(prev => ({...prev, debtAndInvestments: Number(e.target.value) || 0}))}
                      placeholder="Enter amount"
                      className="w-32 p-2 rounded-lg bg-white bg-opacity-20 text-white border border-white border-opacity-30 focus:outline-none focus:ring-2 focus:ring-primary-gold placeholder-white placeholder-opacity-50 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                </div>

                {/* Results */}
                {(() => {
                  const results = calculateEmergencyFund();
                  return (
                    <>
                      <div className="bg-white bg-opacity-20 rounded-2xl p-6 mb-4 text-center">
                        <div className="text-sm opacity-90 mb-2">Total Monthly Expenses</div>
                        <div className="text-2xl font-bold text-primary-gold">
                          ₹{results.totalMonthlyExpenses.toLocaleString('en-IN')}
                        </div>
                      </div>
                      
                      <div className="bg-white bg-opacity-20 rounded-2xl p-6 text-center">
                        <div className="text-sm opacity-90 mb-2">You should have</div>
                        <div className="text-2xl font-bold text-primary-gold mb-2">
                          ₹{results.emergencyFundAmount.toLocaleString('en-IN')}
                        </div>
                        <div className="text-sm opacity-90">in any of the 'Liquid Mutual Fund Scheme' for your emergency requirements</div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Financial Planning */}
      <section className="section-padding bg-primary-green text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-poppins font-semibold mb-6">Why Financial Planning is Essential</h2>
            <p className="text-lg mb-12 opacity-90">
              In today's complex financial landscape, having a well-structured financial plan is not a luxury—it's a necessity.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="font-poppins font-semibold mb-3">Goal Achievement</h3>
                <p className="text-sm opacity-80">Turn your dreams into achievable financial targets with systematic planning</p>
              </div>
              
              <div>
                <div className="text-4xl mb-4">⚖️</div>
                <h3 className="font-poppins font-semibold mb-3">Risk Management</h3>
                <p className="text-sm opacity-80">Protect your wealth and family through proper insurance and emergency planning</p>
              </div>
              
              <div>
                <div className="text-4xl mb-4">📈</div>
                <h3 className="font-poppins font-semibold mb-3">Wealth Creation</h3>
                <p className="text-sm opacity-80">Build long-term wealth through disciplined investing and compound growth</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact-section" className="section-padding bg-light-gold">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="heading-section mb-6">Ready to Plan Your Financial Future?</h2>
            <p className="text-large mb-8">
              Take the first step towards financial freedom with our comprehensive financial planning services tailored to your unique needs.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-2xl p-6 text-center">
                <div className="text-primary-gold mb-3">
                  <BookOpen size={32} className="mx-auto" />
                </div>
                <h3 className="font-poppins font-semibold text-primary-green mb-2">Comprehensive Analysis</h3>
                <p className="text-body text-sm">Complete financial health check and goal assessment</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center">
                <div className="text-primary-gold mb-3">
                  <Target size={32} className="mx-auto" />
                </div>
                <h3 className="font-poppins font-semibold text-primary-green mb-2">Customized Strategy</h3>
                <p className="text-body text-sm">Personalized financial plan based on your goals</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center">
                <div className="text-primary-gold mb-3">
                  <Users size={32} className="mx-auto" />
                </div>
                <h3 className="font-poppins font-semibold text-primary-green mb-2">Ongoing Support</h3>
                <p className="text-body text-sm">Regular reviews and plan adjustments as needed</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleScheduleConsultation}
                className="btn-primary"
              >
                Schedule Free Consultation
              </button>
              {isMobile && (
                <button 
                  onClick={handleGetFinancialPlanQuote}
                  className="btn-secondary"
                >
                  Get Financial Plan Quote
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinancialPlanningPage;